/** XeAgents.ai - Dunning Configuration Management */
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { formatDate } from "@/lib/date-format";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { Loader2, RefreshCw, Mail, AlertTriangle, Clock, Settings2, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface DunningConfig {
  id: string;
  gateway: string;
  maxRetries: number;
  retryIntervalDays: number[];
  gracePeriodDays: number;
  suspendAfterGracePeriod: boolean;
  sendEmailOnRetry: boolean;
  sendEmailOnSuspension: boolean;
  sendEmailOnRecovery: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface DunningAttempt {
  id: string;
  userId: string;
  userEmail: string;
  subscriptionId: string;
  invoiceId: string;
  gateway: string;
  attemptNumber: number;
  status: string;
  scheduledAt: string;
  attemptedAt: string | null;
  nextRetryAt: string | null;
  amountDue: string;
  currency: string;
  resultMessage: string | null;
  emailSentAt: string | null;
  createdAt: string;
}

const GATEWAYS = [
  { value: "default", label: "Default (All Gateways)" },
  { value: "stripe", label: "Stripe" },
  { value: "razorpay", label: "Razorpay" },
  { value: "paypal", label: "PayPal" },
  { value: "paystack", label: "Paystack" },
  { value: "mercadopago", label: "MercadoPago" },
];

export default function DunningConfigManagement() {
  const { toast } = useToast();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<DunningConfig | null>(null);
  const [retryIntervals, setRetryIntervals] = useState<number[]>([1, 3, 5, 7]);

  const [formData, setFormData] = useState({
    gateway: "default",
    maxRetries: 4,
    gracePeriodDays: 7,
    suspendAfterGracePeriod: true,
    sendEmailOnRetry: true,
    sendEmailOnSuspension: true,
    sendEmailOnRecovery: true,
    isActive: true,
  });

  const { data: configs, isLoading: configsLoading } = useQuery<DunningConfig[]>({
    queryKey: ["/api/admin/dunning/configs"],
  });

  const { data: attempts, isLoading: attemptsLoading, refetch: refetchAttempts } = useQuery<DunningAttempt[]>({
    queryKey: ["/api/admin/dunning/attempts"],
  });

  const { data: stats } = useQuery<{
    pending: number;
    failed: number;
    succeeded: number;
    exhausted: number;
  }>({
    queryKey: ["/api/admin/dunning/stats"],
  });

  const updateConfig = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      return apiRequest("PATCH", `/api/admin/dunning/configs/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/dunning/configs"] });
      toast({ title: "Configuration Updated", description: "Dunning configuration has been saved" });
      setIsEditDialogOpen(false);
    },
    onError: (error: any) => {
      toast({ title: "Failed to Update", description: error.message, variant: "destructive" });
    },
  });

  const createConfig = useMutation({
    mutationFn: async (data: any) => {
      return apiRequest("POST", "/api/admin/dunning/configs", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/dunning/configs"] });
      toast({ title: "Configuration Created", description: "New dunning configuration has been created" });
      setIsEditDialogOpen(false);
    },
    onError: (error: any) => {
      toast({ title: "Failed to Create", description: error.message, variant: "destructive" });
    },
  });

  const retryNow = useMutation({
    mutationFn: async (attemptId: string) => {
      return apiRequest("POST", `/api/admin/dunning/attempts/${attemptId}/retry`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/dunning/attempts"] });
      toast({ title: "Retry Initiated", description: "Payment retry has been scheduled" });
    },
    onError: (error: any) => {
      toast({ title: "Retry Failed", description: error.message, variant: "destructive" });
    },
  });

  const cancelAttempt = useMutation({
    mutationFn: async (attemptId: string) => {
      return apiRequest("POST", `/api/admin/dunning/attempts/${attemptId}/cancel`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/dunning/attempts"] });
      toast({ title: "Attempt Cancelled", description: "Dunning attempt has been cancelled" });
    },
    onError: (error: any) => {
      toast({ title: "Cancel Failed", description: error.message, variant: "destructive" });
    },
  });

  const openEditDialog = (config: DunningConfig | null) => {
    if (config) {
      setEditingConfig(config);
      setFormData({
        gateway: config.gateway,
        maxRetries: config.maxRetries,
        gracePeriodDays: config.gracePeriodDays,
        suspendAfterGracePeriod: config.suspendAfterGracePeriod,
        sendEmailOnRetry: config.sendEmailOnRetry,
        sendEmailOnSuspension: config.sendEmailOnSuspension,
        sendEmailOnRecovery: config.sendEmailOnRecovery,
        isActive: config.isActive,
      });
      setRetryIntervals(config.retryIntervalDays);
    } else {
      setEditingConfig(null);
      setFormData({
        gateway: "default",
        maxRetries: 4,
        gracePeriodDays: 7,
        suspendAfterGracePeriod: true,
        sendEmailOnRetry: true,
        sendEmailOnSuspension: true,
        sendEmailOnRecovery: true,
        isActive: true,
      });
      setRetryIntervals([1, 3, 5, 7]);
    }
    setIsEditDialogOpen(true);
  };

  const addRetryInterval = () => {
    const lastInterval = retryIntervals[retryIntervals.length - 1] || 0;
    setRetryIntervals([...retryIntervals, lastInterval + 2]);
  };

  const removeRetryInterval = (index: number) => {
    setRetryIntervals(retryIntervals.filter((_, i) => i !== index));
  };

  const updateRetryInterval = (index: number, value: number) => {
    const newIntervals = [...retryIntervals];
    newIntervals[index] = value;
    setRetryIntervals(newIntervals);
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      pending: "outline",
      succeeded: "default",
      failed: "destructive",
      exhausted: "secondary",
    };
    return <Badge variant={variants[status] || "outline"}>{status}</Badge>;
  };

  const getGatewayLabel = (gateway: string) => {
    return GATEWAYS.find((g) => g.value === gateway)?.label || gateway;
  };

  const formatCurrency = (amount: string, currency: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
    }).format(parseFloat(amount));
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Pending Retries</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">{stats?.pending || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Successful</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats?.succeeded || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Failed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{stats?.failed || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Exhausted</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-600">{stats?.exhausted || 0}</div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="config">
        <TabsList>
          <TabsTrigger value="config">
            <Settings2 className="h-4 w-4 mr-2" />
            Configuration
          </TabsTrigger>
          <TabsTrigger value="attempts">
            <RefreshCw className="h-4 w-4 mr-2" />
            Active Attempts
          </TabsTrigger>
        </TabsList>

        <TabsContent value="config" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Settings2 className="h-5 w-5" />
                    Dunning Configuration
                  </CardTitle>
                  <CardDescription>
                    Configure automatic payment retry behavior for failed payments.
                  </CardDescription>
                </div>
                <Button onClick={() => openEditDialog(null)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Configuration
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {configsLoading ? (
                <div className="flex justify-center p-8">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : (
                <div className="border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Gateway</TableHead>
                        <TableHead>Max Retries</TableHead>
                        <TableHead>Retry Intervals</TableHead>
                        <TableHead>Grace Period</TableHead>
                        <TableHead>Email Notifications</TableHead>
                        <TableHead>Suspend</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {!configs || configs.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                            No dunning configurations. Click "Add Configuration" to create one.
                          </TableCell>
                        </TableRow>
                      ) : (
                        configs.map((config) => (
                          <TableRow key={config.id}>
                            <TableCell className="font-medium">{getGatewayLabel(config.gateway)}</TableCell>
                            <TableCell>{config.maxRetries}</TableCell>
                            <TableCell>
                              <code className="text-xs bg-muted px-2 py-1 rounded">
                                [{config.retryIntervalDays.join(", ")}] days
                              </code>
                            </TableCell>
                            <TableCell>{config.gracePeriodDays} days</TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                {config.sendEmailOnRetry && <Badge variant="outline" className="text-xs">Retry</Badge>}
                                {config.sendEmailOnSuspension && <Badge variant="outline" className="text-xs">Suspend</Badge>}
                                {config.sendEmailOnRecovery && <Badge variant="outline" className="text-xs">Recovery</Badge>}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant={config.suspendAfterGracePeriod ? "destructive" : "secondary"}>
                                {config.suspendAfterGracePeriod ? "Yes" : "No"}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button variant="ghost" size="sm" onClick={() => openEditDialog(config)}>
                                Edit
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>How Dunning Works</AlertTitle>
            <AlertDescription>
              When a payment fails, the system automatically schedules retries based on the configured intervals.
              After all retries are exhausted and the grace period ends, the account can be suspended.
              Email notifications keep customers informed at each stage.
            </AlertDescription>
          </Alert>
        </TabsContent>

        <TabsContent value="attempts" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <RefreshCw className="h-5 w-5" />
                    Active Dunning Attempts
                  </CardTitle>
                  <CardDescription>
                    Monitor and manage ongoing payment retry attempts.
                  </CardDescription>
                </div>
                <Button variant="outline" onClick={() => refetchAttempts()}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {attemptsLoading ? (
                <div className="flex justify-center p-8">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : (
                <div className="border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Gateway</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Attempt #</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Scheduled</TableHead>
                        <TableHead>Next Retry</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {!attempts || attempts.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                            No active dunning attempts. Failed payments will appear here.
                          </TableCell>
                        </TableRow>
                      ) : (
                        attempts.map((attempt) => (
                          <TableRow key={attempt.id}>
                            <TableCell>
                              <div className="font-medium">{attempt.userEmail}</div>
                              <div className="text-xs text-muted-foreground">{attempt.userId.slice(0, 8)}...</div>
                            </TableCell>
                            <TableCell>{attempt.gateway}</TableCell>
                            <TableCell className="font-mono">
                              {formatCurrency(attempt.amountDue, attempt.currency)}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{attempt.attemptNumber}</Badge>
                            </TableCell>
                            <TableCell>{getStatusBadge(attempt.status)}</TableCell>
                            <TableCell className="text-sm">
                              {formatDate(attempt.scheduledAt, 'MMM d, yyyy')}
                            </TableCell>
                            <TableCell className="text-sm">
                              {attempt.nextRetryAt
                                ? formatDate(attempt.nextRetryAt, 'MMM d, yyyy')
                                : "-"}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                {attempt.status === "pending" && (
                                  <>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => retryNow.mutate(attempt.id)}
                                      disabled={retryNow.isPending}
                                    >
                                      Retry Now
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => cancelAttempt.mutate(attempt.id)}
                                      disabled={cancelAttempt.isPending}
                                    >
                                      Cancel
                                    </Button>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit/Create Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingConfig ? "Edit Configuration" : "Add Configuration"}</DialogTitle>
            <DialogDescription>
              Configure dunning behavior for payment retries.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            <div className="space-y-2">
              <Label>Payment Gateway</Label>
              <Select
                value={formData.gateway}
                onValueChange={(value) => setFormData({ ...formData, gateway: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GATEWAYS.map((gw) => (
                    <SelectItem key={gw.value} value={gw.value}>
                      {gw.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Max Retries</Label>
                <Input
                  type="number"
                  min={1}
                  max={10}
                  value={formData.maxRetries}
                  onChange={(e) => setFormData({ ...formData, maxRetries: parseInt(e.target.value) || 4 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Grace Period (Days)</Label>
                <Input
                  type="number"
                  min={0}
                  max={30}
                  value={formData.gracePeriodDays}
                  onChange={(e) => setFormData({ ...formData, gracePeriodDays: parseInt(e.target.value) || 7 })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Retry Intervals (Days after failure)</Label>
                <Button variant="outline" size="sm" onClick={addRetryInterval}>
                  <Plus className="h-3 w-3 mr-1" />
                  Add
                </Button>
              </div>
              <div className="space-y-2">
                {retryIntervals.map((interval, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground w-20">Retry {index + 1}:</span>
                    <Input
                      type="number"
                      min={1}
                      className="w-24"
                      value={interval}
                      onChange={(e) => updateRetryInterval(index, parseInt(e.target.value) || 1)}
                    />
                    <span className="text-sm text-muted-foreground">days</span>
                    {retryIntervals.length > 1 && (
                      <Button variant="ghost" size="icon" onClick={() => removeRetryInterval(index)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-4 pt-4 border-t">
              <Label className="text-base font-semibold">Email Notifications</Label>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <Label className="font-normal">Email on retry scheduled</Label>
                  </div>
                  <Switch
                    checked={formData.sendEmailOnRetry}
                    onCheckedChange={(checked) => setFormData({ ...formData, sendEmailOnRetry: checked })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-destructive" />
                    <Label className="font-normal">Email on account suspension</Label>
                  </div>
                  <Switch
                    checked={formData.sendEmailOnSuspension}
                    onCheckedChange={(checked) => setFormData({ ...formData, sendEmailOnSuspension: checked })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-green-600" />
                    <Label className="font-normal">Email on payment recovery</Label>
                  </div>
                  <Switch
                    checked={formData.sendEmailOnRecovery}
                    onCheckedChange={(checked) => setFormData({ ...formData, sendEmailOnRecovery: checked })}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-4 pt-4 border-t">
              <Label className="text-base font-semibold">Account Actions</Label>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="font-normal">Suspend account after grace period</Label>
                  <Switch
                    checked={formData.suspendAfterGracePeriod}
                    onCheckedChange={(checked) => setFormData({ ...formData, suspendAfterGracePeriod: checked })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="font-normal">Configuration active</Label>
                  <Switch
                    checked={formData.isActive}
                    onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                const data = { ...formData, retryIntervalDays: retryIntervals };
                if (editingConfig) {
                  updateConfig.mutate({ id: editingConfig.id, data });
                } else {
                  createConfig.mutate(data);
                }
              }}
              disabled={updateConfig.isPending || createConfig.isPending}
            >
              {(updateConfig.isPending || createConfig.isPending) && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {editingConfig ? "Save Changes" : "Create Configuration"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
